import React, { useState } from 'react';
import { Card, Button, Table, Modal, Form, Input, DatePicker, App, Descriptions } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import dayjs from 'dayjs';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const MeetingModePage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [form] = Form.useForm();
  const { data, isLoading } = useQuery({
    queryKey: ['owner-intelligence', 'meetings'],
    queryFn: () => ownerIntelligenceService.listMeetings(),
  });

  const createMeeting = async () => {
    try {
      const values = await form.validateFields();
      await ownerIntelligenceService.createMeeting({
        meeting_title: values.meeting_title,
        meeting_date: values.meeting_date.format('YYYY-MM-DD'),
        agenda: values.agenda ? values.agenda.split('\n').filter(Boolean) : [],
      });
      message.success('Meeting scheduled');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'meetings'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const meetingRows = (data as any[]) ?? [];

  return (
    <OiPageFrame
      subtitle="Board and review meeting mode — agenda, KPI review, decision capture, and follow-up tracking."
      loading={isLoading && !data}
    >
      <Card
        title="Meeting sessions"
        bordered={false}
        extra={<Button type="primary" onClick={() => setOpen(true)}>Schedule meeting</Button>}
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={meetingRows}
          locale={{
            emptyText: (
              <OiEmptyState
                title="No meetings scheduled"
                body="Schedule a review session to walk through business health, cash, risks, and open actions."
                actionLabel="Schedule meeting"
                onAction={() => setOpen(true)}
              />
            ),
          }}
          columns={[
            { title: 'Title', dataIndex: 'meeting_title' },
            { title: 'Date', dataIndex: 'meeting_date' },
            { title: 'Status', dataIndex: 'status' },
            {
              title: '',
              key: 'view',
              render: (_: unknown, row: Record<string, unknown>) => (
                <Button type="link" size="small" onClick={() => setDetail(row)}>
                  View
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Modal title="Schedule review meeting" open={open} onCancel={() => setOpen(false)} onOk={createMeeting}>
        <Form form={form} layout="vertical" initialValues={{ meeting_date: dayjs() }}>
          <Form.Item name="meeting_title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Monthly board review" />
          </Form.Item>
          <Form.Item name="meeting_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="agenda" label="Agenda (one item per line)">
            <Input.TextArea rows={4} placeholder="Business health review&#10;Cash & WC&#10;Risk register&#10;Actions follow-up" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal title="Meeting session" open={Boolean(detail)} onCancel={() => setDetail(null)} footer={null}>
        {detail ? (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Title">{String(detail.meeting_title ?? '')}</Descriptions.Item>
              <Descriptions.Item label="Date">{String(detail.meeting_date ?? '')}</Descriptions.Item>
              <Descriptions.Item label="Status">{String(detail.status ?? '')}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button onClick={() => navigate('/owner-intelligence/board-packs')}>Board packs</Button>
              <Button onClick={() => navigate('/owner-intelligence/actions?tab=decisions')}>Decision log</Button>
              <Button type="primary" onClick={() => navigate('/owner-intelligence/business-health')}>Review health</Button>
            </div>
          </>
        ) : null}
      </Modal>
    </OiPageFrame>
  );
};

export default MeetingModePage;
